import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AppTheme, Project, HealthRAG, ProjectLifecycleStatus } from '../types';
import { listProjects, getProjectsAveragePercentComplete } from '../services/projectService';
import { getTenantPool } from '../services/resourcePoolService';
import { isPortfolioManagerOrAbove } from '../services/portfolioRoleService';
import ResourceWorkloadView from './ResourceWorkloadView';
import { dvList } from '../api/dataverseClient';

interface Props { pca: IPublicClientApplication; theme: AppTheme; }

const STATUS_LABEL: Record<number, string> = {
  100000000: 'Draft', 100000001: 'Active', 100000002: 'On Hold', 100000003: 'At Risk',
  100000004: 'Complete', 100000005: 'Cancelled', 100000006: 'Archived',
};
const STATUS_PILL_VARIANT: Record<number, string> = {
  [ProjectLifecycleStatus.Draft]: 'neutral',
  [ProjectLifecycleStatus.Active]: '',
  [ProjectLifecycleStatus.OnHold]: 'amber',
  [ProjectLifecycleStatus.AtRisk]: 'red',
  [ProjectLifecycleStatus.Complete]: 'green',
  [ProjectLifecycleStatus.Cancelled]: 'neutral',
  [ProjectLifecycleStatus.Archived]: 'neutral',
};
const HEALTH_COLOR: Record<number, string> = {
  [HealthRAG.Green]: 'var(--success)',
  [HealthRAG.Amber]: 'var(--warning)',
  [HealthRAG.Red]:   'var(--danger)',
};
const HEALTH_LABEL: Record<number, string> = {
  [HealthRAG.Green]: 'On Track', [HealthRAG.Amber]: 'At Risk', [HealthRAG.Red]: 'Off Track',
};

// Milestone status: Complete = 100000002 (verified via EntityDefinitions).
const MILESTONE_COMPLETE = 100000002;

interface PortfolioMilestone {
  dagrun_pm_milestoneid: string;
  dagrun_pm_name: string;
  dagrun_pm_duedate: string | null;
  dagrun_pm_status: number | null;
  _dagrun_pm_projectid_value: string | null;
  // Resolved client-side from the projects list — the lookup's name is not
  // directly $select-able (selecting dagrun_pm_projectidname 400s).
  projectName?: string | null;
}

function SegBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        border: 'none',
        background: active ? 'var(--brand-mist)' : 'transparent',
        color: active ? 'var(--brand)' : 'var(--ink-3)',
        fontWeight: active ? 500 : 400, fontFamily: 'var(--font-ui)', fontSize: 12,
        padding: '5px 12px', borderRadius: 4, cursor: 'pointer',
      }}>{children}</button>
  );
}

function StatCard({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div className="card" style={{ padding: 20, flex: 1, minWidth: 150 }}>
      <div className="eyebrow">{label}</div>
      <div style={{
        fontFamily: 'var(--font-display)', fontWeight: 200, fontSize: 36,
        color: 'var(--brand)', lineHeight: 1, marginTop: 8,
      }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

export default function Portfolio({ pca }: Props) {
  const nav = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [milestones, setMilestones] = useState<PortfolioMilestone[]>([]);
  const [loading, setLoading]   = useState(true);
  const [view, setView] = useState<'overview' | 'workload'>('overview');
  const [poolExists, setPoolExists] = useState(false);
  const [canViewWorkload, setCanViewWorkload] = useState(false);

  // The Workload tab only appears when a global resource pool is configured
  // AND the user is a project manager (or above). Users with no role are
  // denied — the tab is hidden, not just its contents.
  useEffect(() => {
    getTenantPool(pca).then(p => setPoolExists(!!p)).catch(() => setPoolExists(false));
    isPortfolioManagerOrAbove(pca).then(setCanViewWorkload).catch(() => setCanViewWorkload(false));
  }, [pca]);
  const showWorkloadTab = poolExists && canViewWorkload;

  useEffect(() => {
    Promise.all([
      listProjects(pca),
      getProjectsAveragePercentComplete(pca),
      dvList<PortfolioMilestone>(pca, 'dagrun_pm_milestones', {
        select: ['dagrun_pm_milestoneid', 'dagrun_pm_name', 'dagrun_pm_duedate', 'dagrun_pm_status', '_dagrun_pm_projectid_value'],
        filter: `dagrun_pm_status ne ${MILESTONE_COMPLETE} and dagrun_pm_duedate ne null`,
        orderby: 'dagrun_pm_duedate asc',
        top: 100,
      }).catch(() => [] as PortfolioMilestone[]),
    ])
      .then(([list, avgMap, ms]) => {
        setProjects(list.map(p => ({ ...p, dagrun_pm_percentcomplete: avgMap.get(p.dagrun_pm_projectid) ?? 0 })));
        const nameById = new Map(list.map(p => [p.dagrun_pm_projectid, p.dagrun_pm_name]));
        setMilestones(ms.map(m => ({ ...m, projectName: m._dagrun_pm_projectid_value ? (nameById.get(m._dagrun_pm_projectid_value) ?? null) : null })));
      })
      .finally(() => setLoading(false));
  }, [pca]);

  const stats = useMemo(() => {
    const total  = projects.length;
    const active = projects.filter(p => p.dagrun_pm_lifecyclestatus === ProjectLifecycleStatus.Active).length;
    const avgPct = total > 0 ? Math.round(projects.reduce((a, p) => a + (p.dagrun_pm_percentcomplete ?? 0), 0) / total) : 0;
    const withIssues = projects.filter(p => (p.dagrun_pm_openissuescount ?? 0) > 0).length;
    const withRisks  = projects.filter(p => (p.dagrun_pm_openriskscount ?? 0) > 0).length;
    return { total, active, avgPct, withIssues, withRisks };
  }, [projects]);

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <h2 style={{ marginBottom: 0 }}>Portfolio</h2>
        <div style={{ display: 'inline-flex', gap: 2, padding: 2, background: 'var(--surface-2)', borderRadius: 'var(--r-sm)' }}>
          <SegBtn active={view === 'overview'} onClick={() => setView('overview')}>Overview</SegBtn>
          {showWorkloadTab && <SegBtn active={view === 'workload'} onClick={() => setView('workload')}>Workload</SegBtn>}
          <SegBtn active={false} onClick={() => nav('/portfolio/timeline')}>Timeline</SegBtn>
        </div>
      </div>

      {view === 'workload' && showWorkloadTab && <ResourceWorkloadView pca={pca} />}

      {view === 'overview' && loading && <div className="spinner" style={{ margin: '40px auto' }} />}

      {view === 'overview' && !loading && (
        <>
          {/* SUMMARY BAR */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
            <StatCard label="TOTAL PROJECTS" value={stats.total} />
            <StatCard label="ACTIVE" value={stats.active} />
            <StatCard label="AVG % COMPLETE" value={`${stats.avgPct}%`} />
            <StatCard label="WITH OPEN ISSUES" value={stats.withIssues} />
            <StatCard label="WITH OPEN RISKS" value={stats.withRisks} />
          </div>

          {/* PROJECT HEALTH GRID */}
          {projects.length === 0 ? (
            <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
              No projects to report on yet.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginBottom: 32 }}>
              {projects.map(p => {
                const issues = p.dagrun_pm_openissuescount ?? 0;
                const risks = p.dagrun_pm_openriskscount ?? 0;
                return (
                  <div key={p.dagrun_pm_projectid} className="card"
                    onClick={() => nav(`/project/${p.dagrun_pm_projectid}`)}
                    style={{ padding: 16, cursor: 'pointer', borderTop: '3px solid var(--brand)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                      <strong style={{ fontSize: 14, color: 'var(--ink)' }}>{p.dagrun_pm_name}</strong>
                      {p.dagrun_pm_healthrag != null && (
                        <span title={HEALTH_LABEL[p.dagrun_pm_healthrag] ?? ''} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
                          fontSize: 10, fontWeight: 600, color: HEALTH_COLOR[p.dagrun_pm_healthrag] ?? 'var(--ink-4)',
                        }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: HEALTH_COLOR[p.dagrun_pm_healthrag] ?? 'var(--ink-4)' }} />
                          {HEALTH_LABEL[p.dagrun_pm_healthrag] ?? ''}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 10 }}>
                      {p.dagrun_pm_ownername ?? 'Unassigned'}
                    </div>
                    <span className={`pill${STATUS_PILL_VARIANT[p.dagrun_pm_lifecyclestatus] ? ` ${STATUS_PILL_VARIANT[p.dagrun_pm_lifecyclestatus]}` : ''}`}>
                      {STATUS_LABEL[p.dagrun_pm_lifecyclestatus] ?? '—'}
                    </span>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)', marginTop: 12 }}>
                      {p.dagrun_pm_percentcomplete ?? 0}% complete
                    </div>
                    <div style={{ background: 'var(--surface-2)', borderRadius: 3, height: 4, overflow: 'hidden', marginTop: 4 }}>
                      <div style={{ height: '100%', width: `${p.dagrun_pm_percentcomplete ?? 0}%`, background: 'var(--brand)' }} />
                    </div>
                    {p.dagrun_pm_nextmilestonedate && (
                      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10 }}>
                        Next: {new Date(p.dagrun_pm_nextmilestonedate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    )}
                    {(issues > 0 || risks > 0) && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                        {issues > 0 && (
                          <span style={{ fontSize: 10, fontWeight: 600, background: '#FEE2E2', color: '#991B1B', padding: '2px 8px', borderRadius: 10 }}>
                            {issues} issue{issues === 1 ? '' : 's'}
                          </span>
                        )}
                        {risks > 0 && (
                          <span style={{ fontSize: 10, fontWeight: 600, background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: 10 }}>
                            {risks} risk{risks === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* PORTFOLIO GANTT — cross-project milestone timeline */}
          <div className="card" style={{ padding: 20 }}>
            <div className="eyebrow" style={{ marginBottom: 14 }}>MILESTONE TIMELINE</div>
            <PortfolioGanttTimeline milestones={milestones} onProjectClick={pid => nav(`/project/${pid}`)} />
          </div>
        </>
      )}
    </div>
  );
}

const DAY_MS = 24 * 60 * 60 * 1000;

function PortfolioGanttTimeline({ milestones, onProjectClick }: {
  milestones: PortfolioMilestone[];
  onProjectClick: (projectId: string) => void;
}) {
  const dated = milestones.filter(m => m.dagrun_pm_duedate);
  if (dated.length === 0) {
    return <div style={{ fontSize: 14, color: 'var(--ink-4)', padding: '20px 0' }}>No active milestones across projects</div>;
  }

  const times = dated.map(m => new Date(m.dagrun_pm_duedate!).getTime());
  const min = Math.min(...times);
  const max = Math.max(...times);
  const span = Math.max(max - min, DAY_MS); // avoid divide-by-zero for a single date

  // Group by project, preserving first-seen order.
  const byProject = new Map<string, { name: string; items: PortfolioMilestone[] }>();
  dated.forEach(m => {
    const pid = m._dagrun_pm_projectid_value ?? '__none';
    const name = m.projectName ?? 'Unknown project';
    if (!byProject.has(pid)) byProject.set(pid, { name, items: [] });
    byProject.get(pid)!.items.push(m);
  });

  const today = Date.now();
  const colorFor = (m: PortfolioMilestone): string => {
    const t = new Date(m.dagrun_pm_duedate!).getTime();
    if (t < today) return '#DC2626';                       // overdue
    if (t - today <= 14 * DAY_MS) return '#D97706';        // upcoming (≤14 days)
    return '#0E7490';                                      // future
  };

  const LABEL_W = 150;
  const fmt = (t: number) => new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <div>
      {/* Axis */}
      <div style={{ display: 'flex', marginBottom: 8 }}>
        <div style={{ width: LABEL_W, flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
          <span>{fmt(min)}</span><span>{fmt((min + max) / 2)}</span><span>{fmt(max)}</span>
        </div>
      </div>
      {[...byProject.entries()].map(([pid, group]) => (
        <div key={pid} style={{ display: 'flex', alignItems: 'center', minHeight: 34, borderTop: '0.5px solid var(--border-light)' }}>
          <button type="button" onClick={() => pid !== '__none' && onProjectClick(pid)}
            title={group.name}
            style={{
              width: LABEL_W, flexShrink: 0, textAlign: 'left', background: 'transparent', border: 'none',
              cursor: pid !== '__none' ? 'pointer' : 'default',
              fontSize: 12, color: pid !== '__none' ? 'var(--brand)' : 'var(--ink-3)',
              fontFamily: 'var(--font-ui)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8,
            }}>{group.name}</button>
          <div style={{ flex: 1, position: 'relative', height: 28 }}>
            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, background: 'var(--border-light)' }} />
            {group.items.map(m => {
              const t = new Date(m.dagrun_pm_duedate!).getTime();
              const pctLeft = ((t - min) / span) * 100;
              const color = colorFor(m);
              return (
                <div key={m.dagrun_pm_milestoneid}
                  title={`${m.dagrun_pm_name} — ${new Date(m.dagrun_pm_duedate!).toLocaleDateString()}`}
                  style={{
                    position: 'absolute', top: '50%', left: `${pctLeft}%`,
                    transform: 'translate(-50%, -50%) rotate(45deg)',
                    width: 11, height: 11, background: color, borderRadius: 2,
                    border: '1px solid #fff', boxShadow: '0 0 0 1px ' + color,
                  }} />
              );
            })}
          </div>
        </div>
      ))}
      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11, color: 'var(--ink-4)' }}>
        <LegendDot color="#DC2626" label="Overdue" />
        <LegendDot color="#D97706" label="Due ≤ 14 days" />
        <LegendDot color="#0E7490" label="Future" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 9, height: 9, background: color, borderRadius: 2, transform: 'rotate(45deg)' }} />
      {label}
    </span>
  );
}
